import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { radii, space, text } from '../../../../styles/onboardingUiTokens';
import { SvgIcon } from '../icons/SvgIcon';

export type AccordionProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Uncontrolled initial open state */
  defaultOpen?: boolean;
  /** Controlled open state */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Chevron stroke color (SVG `primary`); defaults to `colors.medium` */
  arrowColor?: string;
  /** Omit bottom divider (e.g. last item in a list) */
  noBorder?: boolean;
  /** Stable prefix for `id` / `aria-*` wiring when you need predictable DOM ids */
  id?: string;
};

export function Accordion({
  title,
  subtitle,
  children,
  className,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  arrowColor = colors.medium,
  noBorder = false,
  id: idPrefix,
}: AccordionProps) {
  const reactId = useId();
  const base = idPrefix ?? `accordion${reactId.replace(/:/g, '')}`;
  const headerId = `${base}-header`;
  const panelId = `${base}-panel`;

  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const expanded = isControlled ? controlledOpen : internalOpen;

  const toggle = () => {
    const next = !expanded;
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  return (
    <Root className={className} $noBorder={noBorder}>
      <HeaderButton type="button" id={headerId} aria-expanded={expanded} aria-controls={panelId} onClick={toggle}>
        <TitleBlock>
          <TitleText>{title}</TitleText>
          {subtitle ? <SubtitleText>{subtitle}</SubtitleText> : null}
        </TitleBlock>
        <ChevronWrap $expanded={expanded} aria-hidden="true">
          <SvgIcon name="arrowright" size={24} primaryColor={arrowColor} />
        </ChevronWrap>
      </HeaderButton>
      <ContentWrapper $expanded={expanded}>
        <ContentInner
          $expanded={expanded}
          aria-hidden={expanded ? undefined : true}
          inert={expanded ? undefined : true}
          id={panelId}
          role="region"
          aria-labelledby={headerId}
        >
          {children}
        </ContentInner>
      </ContentWrapper>
    </Root>
  );
}

const Root = styled.div<{ $noBorder: boolean }>`
  width: 100%;
  padding: ${space.sm} 0;
  border-bottom: ${({ $noBorder }) => ($noBorder ? 'none' : `1px solid ${colors.light}`)};
`;

const HeaderButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md};
  margin: 0;
  padding: ${space.xs} 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: ${radii.sm};
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const TitleText = styled.div`
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 700;
  color: ${colors.dark};
`;

const SubtitleText = styled.div`
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  color: ${colors.medium};
`;

const ChevronWrap = styled.span<{ $expanded: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '-90deg' : '90deg')});
`;

const ContentWrapper = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 0.25s ease;
`;

const ContentInner = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  min-height: 0;
  padding-top: ${({ $expanded }) => ($expanded ? space.sm : 0)};
  transition: padding-top 0.25s ease;
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  color: ${colors.dark};
`;
